<script setup lang="ts">
import { computed } from 'vue';

import type { SystemListMessage, SystemOptionEvent, SystemOption } from '@/library/message/types';
import { isArticleOption, isCategoryOption, isSectionOption } from '@/library/message/lib';
import { MessageType, SystemEvent } from '@/library/message/enum';

import ChatMessageWrapper from './ChatMessageWrapper.vue';
import MessageActions from '../MessageActions.vue';
import type { ActionItem } from '../MessageActions.vue';

export interface SystemListMessageProps {
  message: SystemListMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
  // variant?: 'categories' | 'sections' | 'articles';
}

const props = defineProps<SystemListMessageProps>();
const emit = defineEmits<{
  (e: 'message:input', payload: SystemOptionEvent): void;
}>();

const selectOption = (selected: SystemOption) => {
  const payload: SystemOptionEvent = {
    type: SystemEvent.LIST_OPTION_SELECTED,
    message: props.message,
    payload: {
      selected,
    },
  };

  emit('message:input', payload);
};

const isTreeList = computed(
  () => props.message.type === MessageType.HYBRID_LIST || props.message.type === MessageType.SECTION_LIST
);

const selectedOptionId = computed(() =>
  typeof props.message.selectedOption === 'string' ? props.message.selectedOption : props.message.selectedOption?.id
);

const isArticleOnlyList = computed(() => props.message.options.every(isArticleOption));

// Single computed property for all action items
const actionItems = computed<ActionItem[]>(() => {
  // Categories List
  if (props.message.type === MessageType.CATEGORIES_LIST) {
    return props.message.options.filter(isCategoryOption).map((option) => ({
      id: option.id,
      label: option.title,
      imageUri: option.iconUri,
      selected: option.id === selectedOptionId.value,
    }));
  }

  // Tree/Section List
  if (isTreeList.value) {
    return props.message.options.map((option) => ({
      id: option.id,
      label: option.title,
      selected: option.id === selectedOptionId.value,
      isSection: isSectionOption(option),
    }));
  }

  // Article-only List
  if (isArticleOnlyList.value) {
    return props.message.options.filter(isArticleOption).map((option) => ({
      id: option.id,
      label: option.title,
      selected: option.id === selectedOptionId.value,
    }));
  }

  return [];
});

// Determine the variant based on message type
const actionsVariant = computed<'categories' | 'tree'>(() => {
  return isTreeList.value ? 'tree' : 'categories';
});

const handleActionSelect = (item: ActionItem) => {
  const option = props.message.options.find((opt) => opt.id === item.id);
  if (option) {
    selectOption(option);
  }
};
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <MessageActions
        v-if="actionItems.length > 0"
        :items="actionItems"
        :variant="actionsVariant"
        @select="handleActionSelect"
      />
    </template>
  </ChatMessageWrapper>
</template>
